import { useApi } from '@/api';
import { TableSkeleton } from '@/components';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import {
  EtherScanPictogram,
  Flex,
  CurrencyValue,
  PercentageValue,
  formatNumber,
  FormattedNumber,
  DataTable,
  RelativeDateTime
} from '@blockanalitica/ui';
import { useMemo } from 'react';

function formatParameter(parameter, value, percents, seconds) {
  if (percents.includes(parameter)) {
    return <PercentageValue value={value} />;
  } else if (seconds.includes(parameter)) {
    return formatNumber(value, { notation: 'standard' }) + ' sec';
  } else if (parameter === 'Stopped') {
    return value === '0' ? 'False' : 'True';
  } else {
    return <FormattedNumber value={value} />;
  }
}

function formatParameterChange(parameter, value, percents, seconds) {
  if (['0E-18', '0', 0].includes(value)) {
    return null;
  } else if (percents.includes(parameter)) {
    return <PercentageValue value={value} options={{ trend: true }} />;
  } else if (seconds.includes(parameter)) {
    return (
      <CurrencyValue
        value={value}
        options={{
          trend: true,
          currency: '',
          iconSize: '0',
          notation: 'standard'
        }}
      />
    );
  } else if (parameter === 'Stopped') {
    return value === '0' ? 'False' : 'True';
  } else {
    return (
      <CurrencyValue
        value={value}
        options={{ trend: true, currency: '', iconSize: '0' }}
      />
    );
  }
}

export default function ParametersTable({ ilk, daysAgo }) {
  const [data, error] = useApi({
    url: `/ilk-parameters/ilk`,
    queryParams: {
      ilk: ilk,
      days_ago: daysAgo
    }
  });

  const percents: string[] = useMemo(() => {
    return ['LR', 'Liquidation ratio', 'Stability fee'];
  }, []);
  const seconds: string[] = useMemo(() => {
    return ['DC IAM ttl'];
  }, []);

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Parameter',
        getCell: (row) => row.name,
        cellAlign: 'flex-start',
        headerAlign: 'flex-start'
      },
      {
        header: 'Value',
        getCell: (row) =>
          formatParameter(row.name, row.value, percents, seconds),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: `${daysAgo}D change`,
        getCell: (row) =>
          formatParameterChange(row.name, row.value_change, percents, seconds),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Last update',
        getCell: (row) =>
          row.tx_hash ? (
            <Flex gap={['xs', '3xs']} color="muted">
              {row.datetime ? <RelativeDateTime value={row.datetime} /> : null}
              <a
                href={`https://etherscan.io/tx/${row.tx_hash}`}
                target="_blank"
                rel="noreferrer">
                <EtherScanPictogram size="0" />
              </a>
            </Flex>
          ) : null,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <DataTable rows={data} columns={columns} getKey={(row) => row.param} />
    );
  }
  return <TableSkeleton />;
}
